<script setup>
import { computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useForm, useField } from 'vee-validate'
import Box from '../../../../../components/Box'
import Loading from '../../../../../components/Loading'
import {
  KkTextField,
  KkValidationError,
  KkFormLabel,
  KkIcon
} from '../../../../../components/base'
import { KkInfo, KkListWithMenu } from '../../../../../components/ui'
import { KkBannerForm } from '../../../../../components/domains/mapionBiz'
const store = useStore()
const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { company_uuid, template_id } = route.params
const template = computed(() => store.state.companiesMapionBizBannersTemplates.data)
const templateLoading = computed(() => store.state.companiesMapionBizBannersTemplates.loading)
const readMapionBizBannersTemplate = (templateId) => store.dispatch('companiesMapionBizBannersTemplates/readMapionBizBannersTemplate', templateId)
const createMapionBizBannersTemplate = (payload) => store.dispatch('companiesMapionBizBannersTemplates/createMapionBizBannersTemplate', payload)
const updateMapionBizBannersTemplate = (templateId, payload) => store.dispatch('companiesMapionBizBannersTemplates/updateMapionBizBannersTemplate', { templateId, payload })
const HEADER_COUNT_LIMIT = 3
const FOOTER_COUNT_LIMIT = 40
const state = reactive({
  headers: [],
  footers: [],
  createHeader: false,
  editHeader: false,
  editHeaderIndex: null,
  openMenuHeaderIndex: null,
  createFooter: false,
  editFooter: false,
  editFooterIndex: null,
  openMenuFooterIndex: null
})

const schema = {
  name: 'required|max:140',
}
const { handleSubmit, setValues, meta } = useForm({
  validationSchema: schema,
  initialValues: {
    name: '',
  },
})
const { value: name, errorMessage: nameError, handleChange: nameHandleChange } = useField('name', undefined, { label: t('pages.companies.mapionBizBannerTemplateForm.setting.name') })

const menuList =   [
  {
    type: 'edit',
    text: t('labels.edit')
  },
  {
    type: 'moveTop',
    text: t('labels.moveTop')
  },
  {
    type: 'moveOneUp',
    text: t('labels.moveOneUp')
  },
  {
    type: 'moveOneDown',
    text: t('labels.moveOneDown')
  },
  {
    type: 'moveBottom',
    text: t('labels.moveBottom')
  },
  {
    type: 'delete',
    text: t('labels.delete')
  },
]

const handleClickMenu = ({ type, index, position }) => {
  const target = state[`${ position }s`]
  if(type === 'edit') {
    const str = position.charAt(0).toUpperCase() + position.slice(1)
    state[`edit${ str }`] = true
    state[`edit${ str }Index`] = index
    return
  }
  if (target.length === 1 && type !== 'delete') return
  if (type === 'moveOneUp' && index === 0) return
  if (type === 'moveBottom' && index === target.length - 1) return
  const condition = target[index]
  target.splice(index, 1)
  switch (type) {
  case 'moveTop':
    target.unshift(condition)
    break
  case 'moveOneUp':
    if (index === 0) return
    target.splice(index - 1, 0, condition)
    break
  case 'moveOneDown':
    target.splice(index + 1, 0, condition)
    break
  case 'moveBottom':
    target.push(condition)
    break
  case 'delete':
    break
  }
}

const saveTemplate = (value, position) => {
  const str = position.charAt(0).toUpperCase() + position.slice(1)
  const target = state[`${ position }s`]
  let index = state[`edit${ str }Index`]
  if(state[`edit${ str }`]) {
    target[index] = value
  } else {
    target.push(value)
  }
  state[`create${ str }`] = false
  state[`edit${ str }`] = false
  index = null
}

const cancelTemplate = (position) => {
  const str = position.charAt(0).toUpperCase() + position.slice(1)
  state[`create${ str }`] = false
  state[`edit${ str }`] = false
  state[`edit${ str }Index`] = null
}

const handleSave = handleSubmit(async (value) => {
  const payload = {
    template: {
      name: value.name,
      headers: state.headers,
      footers: state.footers
    }
  }
  const isSuccess = template_id ? await updateMapionBizBannersTemplate(template_id, payload) : await createMapionBizBannersTemplate(payload)
  if(isSuccess) router.push({ name: 'CompaniesMapionBizBannerTemplates', params: { company_uuid: company_uuid } })
})

const validation = computed(() => {
  return meta.value.valid &&
         (state.headers.length !== 0 || state.footers.length !== 0) &&
         !state.createHeader && !state.createFooter &&
         !state.editHeader && !state.editFooter
})

const initData = () => {
  setValues({
    name: template.value.name,
  })
  state.headers = template.value.headers
  state.footers = template.value.footers
}

const load = async () => {
  await readMapionBizBannersTemplate(template_id || route.query.template_id)
  initData()
}

if(template_id || route.query.template_id) load()
</script>
<template>
  <div>
    <Loading v-if="templateLoading" />
    <div v-else class="p-box">
      <div class="p-box__header">
        <div class="backToIndex">
          <router-link class="c-button__item c-button__item--outline" :to="{ name: 'CompaniesMapionBizBannerTemplates', params: { company_uuid: company_uuid } }">
            {{ $t("pages.companies.mapionBizBannerTemplateForm.backToIndex") }}
          </router-link>
        </div>
      </div>
      <div class="p-box__body">
        <div class="c-grid">
          <div class="c-grid__items">
            <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__7 c-grid__item__l__7">
              <Box>
                <template #header>
                  {{ $t('pages.companies.mapionBizBannerTemplateForm.setting.name') }}
                </template>
                <template #body>
                  <KkInfo icon="hint">
                    {{ $t('pages.companies.mapionBizBannerTemplateForm.setting.info') }}
                  </KkInfo>
                  <div class="formContainer">
                    <KkFormLabel :text="`${ t('pages.companies.mapionBizBannerTemplateForm.setting.name') }(${ $t('labels.countCharacters', { count: name.length, max: 140 }) })${ t('labels.requiredLabel') }`" />
                    <KkTextField :model-value="name" class="c-textField" :placeholder="$t('pages.companies.mapionBizBannerTemplateForm.setting.placeholder')" @update:model-value="nameHandleChange" />
                    <p class="example">{{ $t('pages.companies.mapionBizBannerTemplateForm.setting.example') }}</p>
                    <KkValidationError :text="nameError" />
                  </div>
                </template>
              </Box>
              <Box>
                <template #header>
                  {{ $t('pages.companies.mapionBizBannerTemplateForm.header.title') }}
                </template>
                <template #body>
                  <KkBannerForm v-if="state.headers.length === 0" position="header" @save="saveTemplate" @cancel="cancelTemplate" />
                  <template v-for="(header, index) in state.headers" :key="`header${ index }`">
                    <KkBannerForm v-if="state.editHeader && state.editHeaderIndex === index" :kind="header.kind" :name="header.name" position="header" @save="saveTemplate" @cancel="cancelTemplate" />
                    <KkListWithMenu
                      v-else
                      class="elementList"
                      :menu-list="menuList"
                      :open="state.openMenuHeaderIndex === index"
                      @on-click-edit-menu="e => handleClickMenu({ type: e, index: index, position: 'header' })"
                      @open="state.openMenuHeaderIndex = index"
                      @close="state.openMenuHeaderIndex = null"
                    >
                      <div class="elementList__kind">{{ $t(`pages.companies.mapionBizBannerTemplateForm.kind.${ header.kind }.label`) }}</div>
                      <div>{{ header.name }}</div>
                    </KkListWithMenu>
                  </template>
                  <KkBannerForm v-if="state.createHeader" position="header" @save="saveTemplate" @cancel="cancelTemplate" />
                  <button v-show="!state.editHeader && !state.createHeader && state.headers.length !== 0 && state.headers.length < HEADER_COUNT_LIMIT" class="addContainer" type="button" @click="state.createHeader = true">
                    <KkIcon name="iconPlusRed" />
                    <span class="addLabel">{{ $t('pages.companies.mapionBizBannerTemplateForm.addLabel') }}</span>
                  </button>
                </template>
              </Box>
              <Box>
                <template #header>
                  {{ $t('pages.companies.mapionBizBannerTemplateForm.footer.title') }}
                </template>
                <template #body>
                  <KkBannerForm v-if="state.footers.length === 0" position="footer" @save="saveTemplate" @cancel="cancelTemplate" />
                  <template v-for="(footer, index) in state.footers" :key="`footer${ index }`">
                    <KkBannerForm v-if="state.editFooter && state.editFooterIndex === index" :kind="footer.kind" :name="footer.name" position="footer" @save="saveTemplate" @cancel="cancelTemplate" />
                    <KkListWithMenu
                      v-else
                      class="elementList"
                      :menu-list="menuList"
                      :open="state.openMenuFooterIndex === index"
                      @on-click-edit-menu="e => handleClickMenu({ type: e, index: index, position: 'footer' })"
                      @open="state.openMenuFooterIndex = index"
                      @close="state.openMenuFooterIndex = null"
                    >
                      <div class="elementList__kind">{{ $t(`pages.companies.mapionBizBannerTemplateForm.kind.${ footer.kind }.label`) }}</div>
                      <div>{{ footer.name }}</div>
                    </KkListWithMenu>
                  </template>
                  <KkBannerForm v-if="state.createFooter" position="footer" @save="saveTemplate" @cancel="cancelTemplate" />
                  <button v-show="!state.editFooter && !state.createFooter && state.footers.length !== 0 && state.footers.length < FOOTER_COUNT_LIMIT" class="addContainer" type="button" @click="state.createFooter = true">
                    <KkIcon name="iconPlusRed" />
                    <span class="addLabel">{{ $t('pages.companies.mapionBizBannerTemplateForm.addLabel') }}</span>
                  </button>
                </template>
              </Box>
            </div>
            <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__5 c-grid__item__l__5">
              <div class="p-box__body">
                <div class="p-box">
                  <div class="p-box__body">
                    <KkInfo icon="hint">
                      {{ $t('pages.companies.mapionBizBannerTemplateForm.info') }}
                    </KkInfo>
                    <div class="c-button">
                      <router-link
                        :to="{ name: 'CompaniesMapionBizBannerTemplates', params: { company_uuid: company_uuid } }"
                        class="c-button__item c-button__item--outline-cancel"
                      >
                        {{ $t('labels.cancel') }}
                      </router-link>
                      <button
                        class="c-button__item c-button__item--primary"
                        :class="{'c-button__item--disabled': !validation}" :disabled="!validation"
                        @click="handleSave"
                      >
                        {{ $t('labels.save') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../../../../../stylesheets/biz_spa/foundation/_var.scss';
.example {
  margin-top: 10px;
}
.backToIndex {
  margin-left: auto;
}
.formContainer {
  margin-top: 10px;
}
.addContainer {
  margin-top: 32px;
  height: 160px;
  width: 100%;
  color: $color-accent-primary;
  background: $color-base-i;
  border-radius: 4px;
  text-align: center;
    svg {
    display: block;
    width: 32px;
    height: 32px;
    margin: 0 auto;
  }
}
.elementList {
  padding: 24px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  margin-bottom: 10px;
  &__kind {
    font-weight: bold;
    margin-bottom: 10px;
  }
}
</style>
